<template>
    <div data-spec-class="network_configuration_fieldset">
        <ColumnTitle>{{ $t('configuratorGeneral.network') }}</ColumnTitle>
        <Radios
            v-if="uplinks.length > 1"
            class="mt-6"
        >
            <template
                v-for="uplink in uplinks"
                :key="uplink.id"
            >
                <Radio
                    :id="uplink.id"
                    v-model="uplinkId"
                    :label="uplink.name"
                    name="uplink"
                />
            </template>
        </Radios>

        <div class="mt-6">
            <Dropdown
                v-model="volumeId"
                name="volume"
                :label="$t('configuratorGeneral.includedBandwidth')"
                :options="volumeDropdownOptions"
            >
            </Dropdown>

            <BaseAlert
                type="info"
                theme="outline"
                class="mt-3 text-left"
                :size="SizeAll.Tiny"
                data-spec-class="network_configuration_fieldset_volume_alert"
            >
                {{ bandwidthHint }}
                <BaseLink
                    class="mt-1 underline"
                    :href="articleUrl"
                    target="_blank"
                >
                    {{ t('actions.moreInfo') }}
                </BaseLink>
            </BaseAlert>

            <Hint class="mt-3">{{ $t('configuratorGeneral.eachMbit') }}</Hint>
        </div>

        <div class="mt-6">
            <div class="-mb-2 flex">
                <CategoryTitle>{{ $t('configuratorGeneral.ddosProtection') }}</CategoryTitle>
                <div>
                    <Badge
                        class="mb-1 ml-2 self-auto"
                        :text="$t('configuratorGeneral.ddosProtectionFree', { count: 20 })"
                        color="blue"
                    />
                </div>
            </div>

            <BaseAlert
                type="info"
                theme="outline"
                class="text-left"
                :size="SizeAll.Tiny"
                data-spec-class="network_configuration_fieldset_ddos_alert"
            >
                {{ t('configuratorGeneral.ddosProtectionHint') }}

                <BaseLink
                    class="mt-1 underline"
                    :href="ddosProtectionUrl"
                >
                    {{ t('actions.moreInfo') }}
                </BaseLink>
            </BaseAlert>
        </div>
    </div>
</template>

<script setup>
import { defineProps, computed } from 'vue';
import ColumnTitle from './Configurator/ColumnTitle.vue';
import Dropdown from './Configurator/Dropdown.vue';
import Radios from './Configurator/Radios.vue';
import Radio from './Configurator/Radio.vue';
import Hint from './Configurator/Hint.vue';
import { useI18n } from 'vue-i18n';
import { SizeAll } from '../../../types.js';
import CategoryTitle from './Configurator/CategoryTitle.vue';
import Badge from './Configurator/Badge.vue';

const { t } = useI18n();

const props = defineProps({
    bandwidthHint: { type: String, default: null },
    uplinks: { type: Array, default: () => [] },
    volumes: { type: Array, required: true },
    fields: { type: Object, required: true },
    selection: { type: Object, required: true },
});

const emit = defineEmits(['update:fields']);

const updateFields = (patch) => {
    emit('update:fields', patch);
};

const uplinkId = computed({
    get: () => props.fields.uplinkId,
    set: (value) => updateFields({ uplinkId: value }),
});

const volumeId = computed({
    get: () => props.fields.volumeId,
    set: (value) => updateFields({ volumeId: value }),
});

const bandwidthHint = computed(
    () => props.bandwidthHint ?? t('configuratorGeneral.includedBandwidthHint.default')
);

const volumeDropdownOptions = computed(() => {
    const options = {};
    Object.values(props.volumes)
        .filter((volume) => {
            return volume.volume <= props.selection.uplink.max_volume * 1000;
        })
        .sort((a, b) => a.volume - b.volume)
        .forEach((volume) => {
            options[volume.id] = `${volume.volume} Mbit/s`;
        });
    return options;
});

const articleUrl =
    'https://service.23m.com/hc/articles/7553053939484-What-does-the-95-traffic-calculation-mean';

const ddosProtectionUrl = '/ddos-protection';
</script>
